import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Link,
  FormHelperText,
  Divider,
  Card,
} from '@mui/material';
import SearchableDropdown from '../../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import {
  userInformationAtom,
  organisationFormDataAtom,
  organisationFormDataErrorAtom,
} from '../../../../Services/_states/index.js';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ManagementActions } from '../../../../Services/_actions/index.js';
import { useSnackBar } from '../../../FlowBuilder/Common/SnackBar/index.tsx';
import { CatagoryTypes } from '../../../../constants.js';
import { redirect, useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import FileInput from '../../../Shared/FileUploadPreview/FileInput.jsx';
import {
  BLOCKED_EXTENSIONS,
  WHATSAPP_ALLOWED_MIME,
  WHATSAPP_LIMITS,
} from '../../../Whatsapp/Template/template.constant.js';

interface OrganizationFormProps {
  onLogoSelect: (file: File | null) => void;
  
}

const OrganizationForm: React.FC = ({ onLogoSelect }) => {
  const loggedInUser = useRecoilValue(userInformationAtom);
  const actions = ManagementActions();
  const [formData, setFormData] = useRecoilState(organisationFormDataAtom);
  const errors = useRecoilValue(organisationFormDataErrorAtom);
  const { mode } = useParams();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);

  const [businessTypes, setBusinessTypes] = useState<any[]>([]);
  const { showSnackBar } = useSnackBar();
const [sessionDays, setSessionDays] = useState<number>(0);
const [sessionHours, setSessionHours] = useState<number>(0);
const [sessionMinutes, setSessionMinutes] = useState<number>(0);

  // Local validation error for subdomain
  const [subdomainError, setSubdomainError] = useState<string>('');

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const getBusinessTypeSubTypes = async () => {
    try {
      const res = await actions.getAllBusinessTypeSubTypes();
      if (res?.status === 200) {
        setBusinessTypes(res?.data || []);
      }
    } catch {
      showSnackBar('Error fetching business type sub types', 'error');
    }
  };

  const calculateTotalMinutes = (
  days: number,
  hours: number,
  minutes: number
) => {
  return days * 24 * 60 + hours * 60 + minutes;
};
useEffect(() => {
  const totalMinutes = calculateTotalMinutes(
    sessionDays,
    sessionHours,
    sessionMinutes
  );

  setFormData((prev) => ({
    ...prev,
    default_session_timeout: totalMinutes,
  }));
}, [sessionDays, sessionHours, sessionMinutes, setFormData]);
useEffect(() => {
  if (!formData?.default_session_timeout) return;

  const total = Number(formData.default_session_timeout);

  const days = Math.floor(total / (24 * 60));
  const hours = Math.floor((total % (24 * 60)) / 60);
  const minutes = total % 60;

  setSessionDays(days);
  setSessionHours(hours);
  setSessionMinutes(minutes);
}, [formData?.default_session_timeout]);


  const onSelectBusinessType = (selectedBusinessTypes: any[]) => {
    setFormData({
      ...formData,
      selectedBusinessTypes,
      business_type: selectedBusinessTypes?.map((item) => item?.id),
      selectedBusinessSubTypes: [], // Clear subtypes when a new type is selected
      business_subtype: [], // keep primitive field in sync
    });
  };

  const onSelectBusinessSubType = (selectedBusinessSubTypes: any[]) => {
    setFormData({
      ...formData,
      selectedBusinessSubTypes,
      business_subtype: selectedBusinessSubTypes?.map((item) => item?.id),
    });
  };

  useEffect(() => {
    getBusinessTypeSubTypes();
    // runs once on mount
    // eslint-disable-next-line
  }, []);

  // Re-hydrate selected types/subtypes when editing and when source lists are ready
  useEffect(() => {
    if (mode !== 'edit') return;
    if (!businessTypes?.length) return;
    if (!formData?.business_type?.length) return;

    const selectedBusinessTypes = businessTypes.filter((obj: any) =>
      formData.business_type.includes(obj.id)
    );

    const allSubtypes = selectedBusinessTypes.flatMap((item: any) => item?.subtypes || []) || [];

    const selectedBusinessSubTypes = allSubtypes.filter((obj: any) =>
      formData?.business_subtype?.includes(obj.id)
    );

    // Avoid unnecessary state updates
    const sameTypes =
      (formData?.selectedBusinessTypes?.length || 0) === selectedBusinessTypes.length &&
      (formData?.selectedBusinessTypes || []).every(
        (t: any, i: number) => t?.id === selectedBusinessTypes[i]?.id
      );

    const sameSubtypes =
      (formData?.selectedBusinessSubTypes?.length || 0) === selectedBusinessSubTypes.length &&
      (formData?.selectedBusinessSubTypes || []).every(
        (t: any, i: number) => t?.id === selectedBusinessSubTypes[i]?.id
      );

    if (sameTypes && sameSubtypes) return;

    setFormData((prev) => ({
      ...prev,
      selectedBusinessTypes,
      selectedBusinessSubTypes,
    }));
  }, [mode, businessTypes, formData?.business_type, formData?.business_subtype, setFormData]);

  const validateSubdomain = (raw: string): string => {
    // if (!raw) return "Subdomain is required";
    const value = raw?.toLowerCase()?.trim();

    if (value.length < 3 || value.length > 63) {
      return 'Subdomain must be 3–63 characters long';
    }
    if (!/^[a-z0-9-]+$/.test(value)) {
      return 'Only lowercase letters, numbers, and hyphens are allowed';
    }
    if (!/^[a-z0-9].*[a-z0-9]$/.test(value)) {
      return 'Must start and end with a letter or number';
    }
    if (/--/.test(value)) {
      return 'Consecutive hyphens are not allowed';
    }
    const reserved = new Set(['www', 'admin', 'root', 'mail']);
    if (reserved.has(value)) {
      return `“${value}” is reserved; choose another subdomain`;
    }
    return '';
  };

  const handleSubdomainChange = (v: string) => {
    const value = v.toLowerCase().replace(/\s+/g, '-');
    setFormData({ ...formData, sub_domain: value });
    setSubdomainError(validateSubdomain(value)); // live validation
  };

  const handleSubdomainBlur = () => {
    setSubdomainError(validateSubdomain(formData?.sub_domain || ''));
  };

  const previewUrl = useMemo(() => {
    const sub = (formData?.sub_domain || '').trim();
    return sub
      ? `https://${sub}${process.env.REACT_APP_SUBDOMAIN_POSTFIX ?? '.machadalo.com'}`
      : `https://<subdomain>${process.env.REACT_APP_SUBDOMAIN_POSTFIX ?? '.machadalo.com'}`;
  }, [formData?.sub_domain]);

  const inferDocType = (mime: string): 'image' | 'video' | 'audio' | 'file' | null => {
    if (WHATSAPP_ALLOWED_MIME.image.includes(mime)) return 'image';
    if (WHATSAPP_ALLOWED_MIME.video.includes(mime)) return 'video';
    if (WHATSAPP_ALLOWED_MIME.audio.includes(mime)) return 'audio';
    if (WHATSAPP_ALLOWED_MIME.file.includes(mime)) return 'file';
    return null;
  };
  const bytesToMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2);

  const MAX_LOGO_SIZE_MB = 2;
  const ALLOWED_LOGO_TYPES = ['image/jpeg', 'image/png'];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const fileName = selectedFile?.name?.toLowerCase();
    const ext = fileName.split('.').pop();
    if (BLOCKED_EXTENSIONS.has(ext)) {
      showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
      event.target.value = '';
      return;
    }
    const typeGroup = inferDocType(selectedFile.type);
    if (!typeGroup) {
      showSnackBar(`Unsupported file type (${selectedFile.type || ext}).`, 'error');
      event.target.value = '';
      return;
    }

    const limit = WHATSAPP_LIMITS[typeGroup];
    if (selectedFile.size > limit) {
      showSnackBar(
        `File too large for ${typeGroup}. Max ${bytesToMB(limit)} MB. Your file is ${bytesToMB(selectedFile?.size)} MB.`,
        'error'
      );
      event.target.value = '';
      return;
    }
    if (!ALLOWED_LOGO_TYPES.includes(selectedFile.type)) {
      showSnackBar('Only JPG and PNG images are allowed as logos.', 'error');
      event.target.value = '';
      return;
    }

    const fileSizeMB = selectedFile.size / (1024 * 1024);
    if (fileSizeMB > MAX_LOGO_SIZE_MB) {
      showSnackBar('Logo image must be smaller than 2 MB.', 'error');
      event.target.value = '';
      return;
    }
    const img = new Image();
    img.src = URL.createObjectURL(selectedFile);
    img.onload = () => {
      const { width, height } = img;
      const MAX_WIDTH = 100;
      const MAX_HEIGHT = 40;

      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        showSnackBar(
          `Logo dimensions too large. Max allowed ${MAX_WIDTH}x${MAX_HEIGHT}px. Your image is ${width}x${height}px.`,
          'error'
        );
        event.target.value = '';
        return;
      }
      setFile(selectedFile);
      onLogoSelect(selectedFile);
      showSnackBar('Logo selected successfully!', 'success');
    };
    // setFile(selectedFile);
    // onLogoSelect(selectedFile);
    // showSnackBar('Logo selected successfully!', 'success');
  };

  // useEffect(() => {
  //   if (mode === 'edit' && formData?.logo_url) {
  //     setFile(null);
  //     onLogoSelect(null);
  //   }
  // }, [mode, formData.logo_url, onLogoSelect]);

  useEffect(() => {
    if (mode === 'edit' && formData?.logo_url) {
      setFile({
        name: formData.logo_url.split('/').pop(),
        previewUrl: formData.logo_url,
        isExisting: true,
      } as any);
    }
  }, [mode, formData.logo_url]);

  useEffect(() => {
    return () => {
      if (file) URL.revokeObjectURL(file as any);
    };
  }, [file]);

  return (
    <Box>
      <Typography
        variant="h5"
        component="h1"
        sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}
      >
        Organisation - Let's start with the basic details
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={
              <Typography component="span">
                Organisation Name <span style={{ color: 'red' }}>*</span>
              </Typography>
            }
            placeholder="Enter Organisation Name"
            variant="outlined"
            size="small"
            value={formData?.name || ''}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors?.name}
            helperText={errors?.name}
          />
        </Grid>

        {/* Mobile Number */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={
              <Typography component="span">
                Mobile Number <span style={{ color: 'red' }}>*</span>
              </Typography>
            }
            placeholder="Enter Mobile Number"
            variant="outlined"
            size="small"
            value={formData?.phone || ''}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            error={!!errors?.phone}
            helperText={errors?.phone}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={
              <Typography component="span">
                Email <span style={{ color: 'red' }}>*</span>
              </Typography>
            }
            placeholder="Enter Email"
            variant="outlined"
            size="small"
            value={formData?.email || ''}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={!!errors?.email}
            helperText={errors?.email}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <SearchableDropdown
            label={
              <Typography component="span">
                Category <span style={{ color: 'red' }}>*</span>
              </Typography>
            }
            options={CatagoryTypes}
            value={
              formData?.category ? { label: formData?.category, value: formData?.category } : null
            }
            onChange={(e: any) => setFormData({ ...formData, category: e?.value || '' })}
            error={!!errors?.category}
            errorMessage={errors?.category}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <SearchableDropdown
            label={
              <Typography component="span">
                Select Business Type <span style={{ color: 'red' }}>*</span>
              </Typography>
            }
            options={businessTypes}
            value={formData?.selectedBusinessTypes || null}
            multiSelect
            labelKey="business_type"
            valueKey="id"
            onChange={onSelectBusinessType}
            error={!!errors?.business_type}
            errorMessage={errors?.business_type}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <SearchableDropdown
            label={
              <Typography component="span">
                Select Business Sub Type <span style={{ color: 'red' }}>*</span>
              </Typography>
            }
            options={
              formData?.selectedBusinessTypes?.flatMap((item: any) => item?.subtypes || []) || []
            }
            labelKey="business_sub_type"
            valueKey="id"
            multiSelect
            value={formData?.selectedBusinessSubTypes || null}
            onChange={onSelectBusinessSubType}
            error={!!errors?.business_subtype}
            errorMessage={errors?.business_subtype}
          />
        </Grid>

        {/* Subdomain */}
        {loggedInUser?.is_superuser && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={
                <Typography component="span">
                  Subdomain
                  {/* <span style={{ color: "red" }}>*</span> */}
                </Typography>
              }
              placeholder="e.g. acmecorp"
              variant="outlined"
              size="small"
              value={formData?.sub_domain || ''}
              onChange={(e) => handleSubdomainChange(e.target.value)}
              onBlur={handleSubdomainBlur}
              error={!!subdomainError || !!errors?.sub_domain}
              helperText={subdomainError || errors?.sub_domain || ''}
              inputProps={{ spellCheck: 'false' }}
            />
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Preview:&nbsp;
              {formData?.sub_domain ? (
                <Link href={previewUrl} target="_blank" underline="hover">
                  {previewUrl}
                </Link>
              ) : (
                <Typography component="span" color="text.secondary">
                  https://&lt;subdomain&gt;
                  {process.env.REACT_APP_SUBDOMAIN_POSTFIX ?? '.machadalo.com'}
                </Typography>
              )}
            </Typography>
          </Grid>
        )}
        {/* Comments */}
        <Grid item xs={12} sm={6}>
          <TextField
            multiline
            fullWidth
            label={<Typography component="span">Comments</Typography>}
            placeholder="Add any notes"
            variant="outlined"
            size="small"
            value={formData?.comments || ''}
            onChange={(e) => handleInputChange('comments', e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
  <Typography variant="subtitle1" fontWeight="bold">
    Default Session Timeout
  </Typography>
</Grid>

<Grid item xs={12} sm={4}>
  <SearchableDropdown
    label="Days"
    options={[...Array(31)].map((_, i) => ({ label: `${i} Days`, value: i }))}
    value={{ label: `${sessionDays} Days`, value: sessionDays }}
    onChange={(v) => setSessionDays(v?.value || 0)}
  />
</Grid>

<Grid item xs={12} sm={4}>
  <SearchableDropdown
    label="Hours"
     options={[...Array(24)].map((_, i) => ({ label: `${i} Hours`, value: i }))}
    value={{ label: `${sessionHours} Hours`, value: sessionHours }}
    onChange={(v) => setSessionHours(v?.value || 0)}
  />
</Grid>

<Grid item xs={12} sm={4}>
  <SearchableDropdown
    label="Minutes"
   options={[...Array(60)].map((_, i) => ({
      label: `${i} Minutes`,
      value: i,
    }))}
    value={{ label: `${sessionMinutes} Minutes`, value: sessionMinutes }}
    onChange={(v) => setSessionMinutes(v?.value || 0)}
  />
</Grid>

        <Grid item xs={12} sm={12}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Upload Organisation Logo
              </Typography>
              <FileInput
                onFileSelect={handleFileChange}
                onFileRemove={() => {
                  setFile(null);
                  onLogoSelect(null);
                  setFormData((prev) => ({
                    ...prev,
                    logo_url: null,
                  }));
                }}
                file={file}
                accept="image/jpeg, image/png"
              />
              <FormHelperText sx={{ mt: 1 }}>Allowed: JPG, PNG • Max 2 MB</FormHelperText>
            </Box>
            {/* {file && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                // position: 'fixed',
                ml: { xs: '10px', sm: '10px', md: '0px' },
                background: '#fff',
                padding: '12px',
                boxShadow: '#e8e8e8 0px 0px 6px 4px',
                borderRadius: '8px'
              }}
            >
              {file && (
                <Box sx={{width:"100px",height:"40px"}}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt="Organisation Logo"
                    style={{ cursor: 'pointer', width: '100%'}}
                  />
                </Box>
              )}
            </Box>
            )} */}

            {file && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  ml: { xs: '10px', sm: '10px', md: '0px' },
                  background: '#fff',
                  padding: '12px',
                  boxShadow: '#e8e8e8 0px 0px 6px 4px',
                  borderRadius: '8px',
                }}
              >
                <Box sx={{ width: '100px', height: '40px' }}>
                  <img
                    src={file.isExisting ? file.previewUrl : URL.createObjectURL(file)}
                    alt="Organisation Logo"
                    style={{ cursor: 'pointer', width: '100%' }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrganizationForm;
